import {useApplicationData} from '@/Core/hooks/useApplicationData';
import {useUnderwritingAnalytics} from '@/Core/hooks/useUnderwritingAnalytics';
import {CalcfocusReportRequest} from '@amplify/athena-models';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Typography,
    makeStyles,
} from '@material-ui/core';
import React, {useEffect, useRef, useState} from 'react';
import {AgenuimContextType} from '../../../Core/agenium/ageniumContext';
import {formatCurrency} from '../../../Core/helpers/formatCurrency';
import {useAgeniumUser} from '../../../Core/hooks/useAgeniumUser';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {IS_PRODUCTION} from '../../../constants';
import {ContactFormStore} from '../../ContactForm/ContactFormStore/ContactFormStore';
import {MAX_COVERAGE_AMOUNT, MIN_COVERAGE_AMOUNT, MIN_PREMIUM_AMOUNT} from '../constants';
import {Outlist} from '../InstantAppModal';
import {getApplicationRequirements, submitDocusign} from '../api/instantIul';
import {useCommonModalStyles} from './common/modalStyles';
import {getAgeniumCalcfocusRequest} from './utils';
import FixedNavigation from './common/FixedNavigation';

const useStyles = makeStyles(() => ({
    warningText: {
        color: '#d32f2f',
        fontWeight: 500,
        textAlign: 'center',
    },
    summaryPanel: {
        margin: '20px auto',
        padding: '16px 24px',
        width: '60%',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        backgroundColor: '#fafafa',
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 0',
        borderBottom: '1px solid #ececec',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    summaryLabel: {
        fontWeight: 500,
    },
    summaryValue: {
        fontFamily: 'monospace',
    },
    dialogValue: {
        fontFamily: 'monospace',
        fontWeight: 600,
    },
}));

interface SendDocusignProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onDocusignSignComplete: () => void;
    ageniumContext: AgenuimContextType;
    onBack?: () => void;
    onDecisionRedirect?: (decision: 'REFERRED' | 'DECLINED' | 'CANCELLED') => void;
    quoteResponse?: Outlist[];
}

export const SendDocusign: React.FC<SendDocusignProps> = ({
    parameterStore,
    contactFormStore,
    onBack,
    onExit,
    onDocusignSignComplete,
    ageniumContext,
    onDecisionRedirect,
    quoteResponse,
}) => {
    const [isAwaitingSignature, setIsAwaitingSignature] = useState(false);
    const [isCheckForDocusignCompleteDone, setIsCheckForDocusignCompleteDone] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const commonClasses = useCommonModalStyles();
    const classes = useStyles();
    const {ageniumUser} = useAgeniumUser();
    const {applicationData} = useApplicationData();
    const hasLoadedAppData = useRef(false);
    const {storeDecision} = useUnderwritingAnalytics({
        applicationId: ageniumContext.applicationId,
        formId: parameterStore.formid,
    });

    function getApplicationField(key: string): string | undefined {
        const raw = ageniumContext.applicationData?.find((t) => t.key === key)?.value;
        return raw == null ? undefined : String(raw);
    }

    function getAgeniumMaxFaceAmount() {
        return Number(getApplicationField('Case_FinancialCalc_MaxFaceAmount'));
    }

    function getEffectiveMaxFaceAmount() {
        const ageniumMax = getAgeniumMaxFaceAmount();
        if (!ageniumMax || Number.isNaN(ageniumMax)) return MAX_COVERAGE_AMOUNT;
        return Math.min(MAX_COVERAGE_AMOUNT, ageniumMax);
    }

    function getDeathBenefit() {
        return Number(quoteResponse?.find((qr) => qr.name === 'Initial death benefit')?.value ?? 0);
    }

    function getMonthlyPremium() {
        const byFieldName = quoteResponse?.find((qr) => qr.fieldName === 'monthlyPremium')?.value;
        const byName = quoteResponse?.find((qr) => qr.name === 'Initial monthly premium')?.value;
        return Number(byFieldName ?? byName ?? 0);
    }

    const hasQuoteData = !!ageniumContext.applicationData && !!quoteResponse;
    const deathBenefit = getDeathBenefit();
    const monthlyPremium = getMonthlyPremium();
    const effectiveMaxFaceAmount = getEffectiveMaxFaceAmount();

    function formatDateDisplay(raw: string | undefined): string | undefined {
        if (!raw) return undefined;
        const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
        const daysInMonth = (y: number, m: number) =>
            [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
        const isValid = (y: number, m: number, day: number) =>
            m >= 1 && m <= 12 && day >= 1 && day <= daysInMonth(y, m);
        // Already in MM/DD/YYYY (how BankingInformation writes effective date).
        const mdy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (mdy && isValid(+mdy[3], +mdy[1], +mdy[2])) return `${mdy[1]}/${mdy[2]}/${mdy[3]}`;
        // YYYY-MM-DD (how Agenium stores DOB); parse directly to avoid the
        // UTC-vs-local-time shift `new Date('2000-01-01')` causes in zones behind UTC.
        const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (ymd && isValid(+ymd[1], +ymd[2], +ymd[3])) return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return raw;
        const yyyy = d.getUTCFullYear();
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        return `${mm}/${dd}/${yyyy}`;
    }

    const firstName = getApplicationField('Case_Applicant_FirstName');
    const lastName = getApplicationField('Case_Applicant_LastName');
    const genderRaw = getApplicationField('Case_RateCalc_Gender');
    const genderDisplay = (() => {
        if (!genderRaw) return undefined;
        if (genderRaw === 'MALE') return 'Male';
        if (genderRaw === 'FEMALE') return 'Female';
        return genderRaw;
    })();
    const dobDisplay = formatDateDisplay(getApplicationField('Case_RateCalc_DateOfBirth'));
    const effectiveDateDisplay = formatDateDisplay(getApplicationField('Case_Applicant_RequestedEffectiveDate'));

    const riskClass = getApplicationField('Case_UWDecision_Overall');
    const tobaccoUseRaw = getApplicationField('Case_RateCalc_TobaccoUse');
    const smokerDisplay = tobaccoUseRaw ? (tobaccoUseRaw !== 'NONSMOKER' ? 'Yes' : 'No') : null;
    const tableRatingRaw = getApplicationField('Case_UWDecision_HxOutcome_TableRating');
    const hasTableRating = !!tableRatingRaw && !['None', 'NotReady', 'NotRated', '0'].includes(tableRatingRaw);
    const flatExtraRaw = getApplicationField('Case_UWDecision_FlatExtraTotalRatePer1000Final');
    const flatExtraNumber = Number(flatExtraRaw);
    const hasFlatExtra = !!flatExtraRaw && !Number.isNaN(flatExtraNumber) && flatExtraNumber > 0;
    const flatExtraDuration = getApplicationField('Case_UWDecision_FlatExtraDuration');
    const flatExtraDisplay = hasFlatExtra
        ? `$${flatExtraNumber} per $1,000${flatExtraDuration ? ` for ${flatExtraDuration}` : ''}`
        : null;

    const summaryRows: Array<{label: string; value: string}> = [
        ...(firstName ? [{label: 'First Name', value: firstName}] : []),
        ...(lastName ? [{label: 'Last Name', value: lastName}] : []),
        ...(genderDisplay ? [{label: 'Gender', value: genderDisplay}] : []),
        ...(dobDisplay ? [{label: 'Date of Birth', value: dobDisplay}] : []),
        ...(riskClass ? [{label: 'Rate Class', value: riskClass}] : []),
        ...(smokerDisplay ? [{label: 'Smoker', value: smokerDisplay}] : []),
        ...(hasTableRating ? [{label: 'Table Rating', value: tableRatingRaw!}] : []),
        ...(flatExtraDisplay ? [{label: 'Flat Extra', value: flatExtraDisplay}] : []),
        {label: 'Face Amount', value: formatCurrency(deathBenefit)},
        {label: 'Monthly Premium', value: formatCurrency(monthlyPremium)},
        ...(effectiveDateDisplay ? [{label: 'Effective Date', value: effectiveDateDisplay}] : []),
    ];

    const validationErrors: string[] = [];
    if (hasQuoteData) {
        if (deathBenefit > effectiveMaxFaceAmount) {
            validationErrors.push(
                `The face amount (${formatCurrency(deathBenefit)}) is greater than the maximum allowed face amount of ${formatCurrency(
                    effectiveMaxFaceAmount,
                )}.`,
            );
        }
        if (deathBenefit < MIN_COVERAGE_AMOUNT) {
            validationErrors.push(
                `The face amount (${formatCurrency(deathBenefit)}) is below the minimum coverage amount of ${formatCurrency(
                    MIN_COVERAGE_AMOUNT,
                )}.`,
            );
        }
        if (monthlyPremium < MIN_PREMIUM_AMOUNT) {
            validationErrors.push(
                `The monthly premium (${formatCurrency(monthlyPremium)}) is below the minimum required premium of ${formatCurrency(
                    MIN_PREMIUM_AMOUNT,
                )}/mo.`,
            );
        }
    }
    const hasValidationErrors = validationErrors.length > 0;

    // Check application decision and redirect if not approved
    useEffect(() => {
        if (
            ageniumContext.applicationDecision &&
            ageniumContext.applicationDecision !== 'APPROVED' &&
            onDecisionRedirect
        ) {
            storeDecision({decision: ageniumContext.applicationDecision});
            onDecisionRedirect(ageniumContext.applicationDecision);
        }
    }, [ageniumContext.applicationDecision, onDecisionRedirect]);

    function onSendDocusignClick() {
        if (hasValidationErrors) return;
        setIsConfirmDialogOpen(true);
    }

    async function onConfirmSendDocusign() {
        setIsConfirmDialogOpen(false);
        setIsAwaitingSignature(true);
        const formId = parameterStore.formid as string;
        const instantIulApplicationId = parameterStore.instantIulApplicationId as string;

        // Use saved prosper data from approval step to prevent modification of quote values
        const savedProsperData = applicationData?.prosperData;
        const calcfocusReportRequest = getAgeniumCalcfocusRequest(
            ageniumContext.getApplicationData(),
            ageniumUser,
            savedProsperData,
        );

        try {
            await submitDocusign(
                formId,
                instantIulApplicationId,
                calcfocusReportRequest as unknown as CalcfocusReportRequest,
            );
            pollForDocusignComplete(formId, instantIulApplicationId);
        } catch (err) {
            console.error('submitDocusign failed', err);
            setIsAwaitingSignature(false);
        }
    }

    async function checkForDocusignComplete(formId: string, applicationId: string) {
        const requirements = await getApplicationRequirements(formId, applicationId);
        return !!requirements?.find((r) => r.type === 'policyDocusign' && r.status !== 'outstanding');
    }

    async function pollForDocusignComplete(formId: string, applicationId: string) {
        const poll = setInterval(async () => {
            const hasMetRequirements = await checkForDocusignComplete(formId, applicationId);

            if (hasMetRequirements) {
                setIsAwaitingSignature(false);
                clearInterval(poll);
                parameterStore.eAppDocusignCompleted = new Date();
                await contactFormStore.saveForm();
                onDocusignSignComplete();
            }
        }, 5000);
    }

    useEffect(() => {
        ageniumContext.initSdk();
        // Check once initial page load in case signed when agent was not on page
        checkForDocusignComplete(
            parameterStore.formid as string,
            parameterStore.instantIulApplicationId as string,
        ).then(async (completed) => {
            if (completed) {
                parameterStore.eAppDocusignCompleted = new Date();
                await contactFormStore.saveForm();
                onDocusignSignComplete();
            } else setIsCheckForDocusignCompleteDone(true);
        });
    }, []);

    // This is required to load application data, initSdk is not enough.
    useEffect(() => {
        if (!hasLoadedAppData.current && ageniumContext.applicationNumber && !ageniumContext.applicationData) {
            hasLoadedAppData.current = true;
            ageniumContext.loadApplicationData();
        }
    }, [ageniumContext.applicationNumber, ageniumContext.applicationData]);

    return (
        <Grid container className={commonClasses.wrapper}>
            <Grid item>
                <Typography variant="h4" className={commonClasses.title}>
                    Let’s Send Over The Docusign
                </Typography>
                <Grid container spacing={9}>
                    <Grid item>
                        <Typography className={commonClasses.text}>
                            Review the values below. Click ‘Send Docusign’ to send the link to the client by email and
                            SMS.
                        </Typography>
                    </Grid>
                    {(!isCheckForDocusignCompleteDone || !ageniumContext.applicationData || isAwaitingSignature) && (
                        <CircularProgress />
                    )}
                </Grid>
            </Grid>

            {hasQuoteData && (
                <Box className={classes.summaryPanel}>
                    {summaryRows.map((row) => (
                        <Box key={row.label} className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>{row.label}</Typography>
                            <Typography className={classes.summaryValue}>{row.value}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            <FixedNavigation
                onBack={onBack}
                isNextDisabled={
                    !isCheckForDocusignCompleteDone ||
                    !ageniumContext.applicationData ||
                    isAwaitingSignature ||
                    hasValidationErrors
                }
                isLoading={false}
                onNext={onSendDocusignClick}
                onNextCustomText="CONFIRM AND SEND DOCUSIGN"
            />

            {validationErrors.length > 0 && (
                <Box style={{margin: '20px auto', width: '60%'}}>
                    {validationErrors.map((message) => (
                        <Typography key={message} className={classes.warningText}>
                            {message}
                        </Typography>
                    ))}
                </Box>
            )}

            <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm DocuSign Submission</DialogTitle>
                <DialogContent>
                    <Typography style={{marginBottom: 16}}>
                        You’re about to send a DocuSign envelope with the following values. This cannot be changed after
                        sending.
                    </Typography>
                    {summaryRows.map((row) => (
                        <Box key={row.label} className={classes.summaryRow}>
                            <Typography className={classes.summaryLabel}>{row.label}</Typography>
                            <Typography className={classes.dialogValue}>{row.value}</Typography>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmDialogOpen(false)} color="default">
                        Cancel
                    </Button>
                    <Button onClick={onConfirmSendDocusign} color="primary" variant="contained" autoFocus>
                        Yes, Send DocuSign
                    </Button>
                </DialogActions>
            </Dialog>

            {!IS_PRODUCTION && (
                <div style={{fontSize: '10px', marginTop: 50}}>
                    <div>applicationData</div>
                    <div style={{color: '#880'}}>{JSON.stringify(ageniumContext.applicationData, null, 2)}</div>
                </div>
            )}
        </Grid>
    );
};
